<script setup>
/**
 * Aceleres -- os canais que um contato reune, um por endereco.
 *
 * A ficha do contato tem UMA coluna de telefone e UM `@` por rede, entao ela
 * representa mal quem junta varios canais -- que virou a regra com Instagram e
 * Messenger, e e o resultado normal de uma mesclagem. Este bloco mostra o que
 * o contato de fato reune, inclusive o que veio de uma mesclagem antiga.
 *
 * Quem decide o que e endereco, o que e grupo e o que e identidade interna e o
 * backend (Aceleres::ContactIdentities), de proposito: a regra tem armadilha
 * (um `@lid` NAO e telefone) e este componente e usado em duas telas. Aqui so
 * se aplica a mascara visual do numero.
 */
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import parsePhoneNumber from 'libphonenumber-js';
import ContactAPI from 'dashboard/api/contacts';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ContactConnectionButton from './ContactConnectionButton.vue';
import { useMapGetter } from 'dashboard/composables/store';
import {
  eligibleInboxesForPhone,
  isEligibleQuickStartInbox,
} from 'dashboard/helper/aceleresQuickStartChannels';
import { inboxDisplayName } from 'dashboard/helper/inboxDisplayName';

const props = defineProps({
  contactPhone: { type: String, default: '' },
  detailView: { type: Boolean, default: false },
  contactId: {
    type: [Number, String],
    default: null,
  },
  // No painel da conversa o espaco e curto: la o bloco so ganha o seu lugar
  // quando o contato de fato reune mais de um endereco -- que e o caso que o
  // atendente precisa entender. Na ficha, onde ha espaco, ele aparece sempre.
  hideWhenSingle: {
    type: Boolean,
    default: false,
  },
  // No modal de mesclar o bloco aparece duas vezes, um por lado: o rotulo do
  // lado ja diz de quem sao os canais, entao o cabecalho sai.
  compact: {
    type: Boolean,
    default: false,
  },
  // No painel da conversa: libera os canais do contato dela para quem pode
  // abrir a conversa, mesmo com o contato fora da visibilidade de contatos.
  conversationId: {
    type: [Number, String],
    default: null,
  },
});

const { t } = useI18n();
const emit = defineEmits(['openConversation']);

const identities = ref([]);
const isLoading = ref(false);
const hasError = ref(false);

const CHANNEL_ICONS = {
  whatsapp: 'i-ri-whatsapp-fill',
  instagram: 'i-ri-instagram-fill',
  messenger: 'i-ri-messenger-fill',
  email: 'i-ri-mail-fill',
  outro: 'i-ri-chat-1-fill',
};

const channelIcon = channel => CHANNEL_ICONS[channel] || CHANNEL_ICONS.outro;

const channelLabel = channel =>
  t(`CONTACT_IDENTITIES.CHANNEL.${String(channel || 'outro').toUpperCase()}`);

// A WAME entrega o numero em E.164. Numero fora do padrao volta como veio --
// nunca inventamos digito.
const formatPhone = value => {
  const digits = String(value || '').replace(/\D/g, '');
  if (!digits) return value || '';
  try {
    const parsed = parsePhoneNumber(`+${digits}`);
    if (parsed?.isValid()) return parsed.formatInternational();
  } catch {
    return value;
  }
  return value;
};

const shortId = value => {
  const text = String(value || '');
  return text.length > 12 ? `${text.slice(0, 6)}…${text.slice(-4)}` : text;
};

// O que o atendente le como endereco. Para o social o `@` e o que ele reconhece;
// sem `@` conhecido sobra o id da rede, que so faz sentido encurtado.
const primaryLabel = identity => {
  if (identity.kind === 'phone') return formatPhone(identity.address);
  if (identity.kind === 'email') return identity.address;
  if (identity.kind === 'group') return t('CONTACT_IDENTITIES.KIND.GROUP');
  if (identity.kind === 'internal')
    return t('CONTACT_IDENTITIES.KIND.INTERNAL');
  if (identity.handle) return `@${identity.handle}`;
  if (identity.channel === 'instagram')
    return t('CONTACT_IDENTITIES.IGSID', { id: shortId(identity.address) });
  if (identity.channel === 'messenger')
    return t('CONTACT_IDENTITIES.PSID', { id: shortId(identity.address) });
  return shortId(identity.address);
};

const connectionsLabel = identity => {
  const connections = identity.connections || [];
  if (connections.length === 0) return '';
  if (connections.length === 1)
    return t('CONTACT_IDENTITIES.VIA_ONE', {
      name: inboxDisplayName(connections[0].inbox_name),
    });
  return t('CONTACT_IDENTITIES.VIA_MANY', { count: connections.length });
};

const conversationsLabel = identity => {
  const count = identity.conversations_count || 0;
  if (!count) return t('CONTACT_IDENTITIES.NO_CONVERSATIONS');
  return t('CONTACT_IDENTITIES.CONVERSATIONS', count, { count });
};

const inboxes = useMapGetter('inboxes/getInboxes');
const currentUser = useMapGetter('getCurrentUser');
const displayedIdentities = computed(() => {
  if (
    !props.detailView ||
    !props.contactPhone ||
    identities.value.some(identity => identity.kind === 'phone')
  )
    return identities.value;
  const userId = Number(currentUser.value?.id);
  return [
    ...identities.value,
    {
      channel: 'whatsapp',
      kind: 'phone',
      address: props.contactPhone,
      source_id: props.contactPhone,
      connections: eligibleInboxesForPhone(inboxes.value, userId)
        .filter(inbox => isEligibleQuickStartInbox(inbox, userId))
        .map(inbox => ({ inbox_id: inbox.id, inbox_name: inbox.name })),
      conversations_count: 0,
    },
  ];
});
const hasIdentities = computed(() => displayedIdentities.value.length > 0);

// Um contato de grupo tem uma identidade so, o proprio grupo: listar isso nao
// acrescenta nada ao que o cabecalho ja diz.
const isGroupOnly = computed(
  () => identities.value.length === 1 && identities.value[0].kind === 'group'
);

// No painel da conversa (`hideWhenSingle`) o bloco e discreto por escolha: nao
// ocupa espaco com carregamento, com erro nem com uma identidade unica, porque
// la ele so existe para explicar o contato que reune varios enderecos.
const shouldRender = computed(() => {
  if (isGroupOnly.value) return false;
  if (isLoading.value || hasError.value || !hasIdentities.value) {
    return !props.hideWhenSingle;
  }
  return !(props.hideWhenSingle && identities.value.length === 1);
});

const fetchIdentities = async id => {
  if (!id) {
    identities.value = [];
    return;
  }
  isLoading.value = true;
  hasError.value = false;
  try {
    const { data } = await ContactAPI.identities(id, {
      conversationId: props.conversationId,
    });
    identities.value = data?.payload || [];
  } catch {
    hasError.value = true;
    identities.value = [];
  } finally {
    isLoading.value = false;
  }
};

watch(() => props.contactId, fetchIdentities, { immediate: true });

defineExpose({ refresh: () => fetchIdentities(props.contactId) });
</script>

<template>
  <section
    v-if="shouldRender"
    class="contact-identities flex w-full flex-col gap-3"
    :class="
      detailView
        ? 'rounded-2xl bg-white p-4 shadow-[0_8px_24px_rgba(0,0,0,0.04)]'
        : compact
          ? ''
          : 'rounded-xl border border-n-weak bg-n-solid-1 p-3 md:p-4'
    "
  >
    <div v-if="!compact" class="flex flex-col gap-1">
      <h6 class="m-0 text-sm font-semibold text-n-slate-12">
        {{
          t(
            detailView
              ? 'CONTACT_DETAIL_VIEW.CHANNELS'
              : 'CONTACT_IDENTITIES.TITLE'
          )
        }}
      </h6>
      <span class="text-xs text-n-slate-11">
        {{
          t(
            detailView
              ? 'CONTACT_DETAIL_VIEW.CHANNELS_HELP'
              : 'CONTACT_IDENTITIES.DESCRIPTION'
          )
        }}
      </span>
    </div>

    <Spinner v-if="isLoading" class="size-4 text-n-slate-10" />

    <span v-else-if="hasError" class="text-xs text-n-slate-11">
      {{ t('CONTACT_IDENTITIES.ERROR') }}
    </span>

    <span v-else-if="!hasIdentities" class="text-xs text-n-slate-11">
      {{ t('CONTACT_IDENTITIES.EMPTY') }}
    </span>

    <ul v-else class="m-0 flex list-none flex-col gap-2 p-0">
      <li
        v-for="identity in displayedIdentities"
        :key="`${identity.channel}-${identity.source_id}`"
        :class="{
          '!grid !grid-cols-[32px_minmax(0,1fr)_auto] !items-center !border !border-n-weak !bg-white !p-3 max-[600px]:!grid-cols-[32px_minmax(0,1fr)]':
            detailView,
        }"
        class="contact-identities__row flex min-w-0 items-start gap-2 rounded-lg bg-n-background p-2.5"
      >
        <span
          :class="[
            channelIcon(identity.channel),
            {
              '!size-8 rounded-lg bg-[#eef9f0] p-2 !text-[#10a344]': detailView,
            },
          ]"
          class="mt-0.5 size-4 shrink-0 text-n-slate-11"
        />
        <div class="flex flex-col min-w-0 gap-0.5">
          <div class="flex flex-wrap items-center gap-1.5">
            <span class="break-all text-sm font-medium text-n-slate-12">
              {{ primaryLabel(identity) }}
            </span>
            <span
              v-if="identity.source === 'merged'"
              class="px-1.5 py-px text-xs rounded-md bg-n-alpha-2 text-n-slate-11"
              :title="
                identity.merged_from_name
                  ? t('CONTACT_IDENTITIES.MERGED_HINT', {
                      name: identity.merged_from_name,
                    })
                  : ''
              "
            >
              {{ t('CONTACT_IDENTITIES.MERGED_BADGE') }}
            </span>
          </div>
          <span class="text-xs text-n-slate-11 break-all">
            {{ channelLabel(identity.channel) }}
            <template v-if="connectionsLabel(identity)">
              • {{ connectionsLabel(identity) }}
            </template>
            <template v-if="identity.source !== 'merged'">
              • {{ conversationsLabel(identity) }}
            </template>
          </span>
          <span
            v-if="identity.kind === 'internal'"
            class="text-xs text-n-slate-10"
          >
            {{ t('CONTACT_IDENTITIES.INTERNAL_HINT') }}
          </span>
        </div>
        <ContactConnectionButton
          v-if="detailView"
          :identity="identity"
          @open-conversation="emit('openConversation', $event)"
        />
      </li>
    </ul>
  </section>
</template>
